import { uniqBy } from 'lodash';
import { combineLatest, map } from 'rxjs';
import type { FormattingRule } from '@ardoq/data-model';
import {
  composeComponentFormattingGroups,
  composeReferencesFormattingGroups,
  ensureUpdatedRuleValue,
  getAddFormattingRuleButtonOptions,
  mapFilterGroups,
  mapFormatting,
  mapFormattingRuleInfo,
  mapListFieldFormatting,
  normalizeFormattingRules,
} from '@ardoq/perspectives';
import { dispatchAction } from '@ardoq/rxbeach';
import { featureOperations, Features } from '@ardoq/features';
import {
  conditionalFormattingAiButtonClicked,
  createFormattingRule,
  deleteFormattingRule,
  keepOriginalColorChanged,
  perspectivesEditor$,
  reorderFormattingRules,
  updateFormattingRule,
} from './perspectivesEditor$';
import { getFilterTabProps } from './getFilterTabProps';
import { activeView$ } from 'streams/views/mainContent/activeView$';
import {
  getViewpointComponentSuggestionLoaders,
  getViewpointReferenceSuggestionLoaders,
} from 'viewpointBuilder/formattingTabs/getFormattingSuggestionLoader';
import {
  buildFieldDataByRuleId,
  getFormattingForInvalidRuleByRuleId,
} from './getFormattingForInvalidRuleByRuleId';
import {
  ensureCollapsedForUnsupportedRules,
  normalizeLegacyCheckboxComparator,
  filterValidConditionalFormattingRules,
  getFormattingRuleNotificationParamsFunction,
} from './formattingUtils';
import { featureContext$ } from 'streams/featureContext$';
import { globalFields$ } from 'streams/fields/globalFields$';
import { fields$ } from 'streams/fields/fields$';
import { getNonTemplateFieldsByName } from 'streams/fields/getNonTemplateFieldsByName';
import { toFieldTypeRecord } from 'streams/fields/toFieldTypeRecord';
import {
  getComponentTypeDetails,
  getReferenceTypeDetails,
  type ComponentTypeInfo,
  type ReferenceTypeDetails,
} from '@ardoq/graph';
import { FormattingTabProps } from '@ardoq/perspectives-sidebar';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { APIEntityType, type APIFieldAttributes } from '@ardoq/api-types';
import { fieldInterface } from '@ardoq/field-interface';
import {
  buildComponentNameAndFieldsIndex,
  buildReferenceNameAndFieldsIndex,
} from './utils';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { type I18nLocale, t, type WithI18nLocale } from '@ardoq/i18n';
import { isPerspectivesAiButtonVisible$ } from './isPerspectivesAiButtonVisible$';

const getUniqueFieldsForTypes = (
  typeEntries: APIFieldAttributes[][],
  datasetFieldNames?: Set<string>
): APIFieldAttributes[] => {
  const unique = uniqBy(typeEntries.flat(), 'name');
  return datasetFieldNames
    ? unique.filter(field => datasetFieldNames.has(field.name))
    : unique;
};

const getFieldsByComponentTypes = (
  componentTypes: ComponentTypeInfo[],
  datasetFieldNames?: Set<string>
): APIFieldAttributes[] =>
  getUniqueFieldsForTypes(
    componentTypes.map(({ componentType: { id }, workspaceModelId }) =>
      fieldInterface.getFieldsByComponentType(id, workspaceModelId)
    ),
    datasetFieldNames
  );

const getFieldsByReferenceTypes = (
  referenceTypes: ReferenceTypeDetails[],
  datasetFieldNames?: Set<string>
): APIFieldAttributes[] =>
  getUniqueFieldsForTypes(
    referenceTypes.map(({ typeId, workspaceModelId }) =>
      fieldInterface.getFieldsByReferenceType(typeId, workspaceModelId)
    ),
    datasetFieldNames
  );

const onFormattingRuleDeleted = (deletedRuleId: string) =>
  dispatchAction(deleteFormattingRule(deletedRuleId));

const onFormattingRuleReordered = (reorderedGroupingRules: string[]) =>
  dispatchAction(reorderFormattingRules(reorderedGroupingRules));

const onFormattingRuleUpdated = (
  updatedFormattingRuleId: string,
  updatedFormattingRule: Partial<FormattingRule>
) =>
  dispatchAction(
    updateFormattingRule({ updatedFormattingRuleId, updatedFormattingRule })
  );

const onFormattingRuleAdded = (newFormattingRule: FormattingRule) =>
  dispatchAction(createFormattingRule(newFormattingRule));

const getAddButtonDisabledPopoverContent =
  (i18nLocale: I18nLocale) => (entitiesName: string) =>
    t(
      i18nLocale,
      'perspectiveSidebar:formattingTab.noEntitiesInDataset',
      { entitiesName },
      `There are no {{entitiesName}} in the opened datasets. Try editing your datasets or open more data.`
    );

const onKeepOriginalColorToggled = (value: boolean) =>
  dispatchAction(keepOriginalColorChanged(value));

const formattingTab$ = combineLatest({
  state: perspectivesEditor$,
  featureContext: featureContext$,
  activeView: activeView$,
  locale: locale$,
  i18nLocale: i18nLocale$,
  globalFields: globalFields$,
  fields: fields$,
  isAiButtonVisible: isPerspectivesAiButtonVisible$,
}).pipe(
  map(
    ({
      state,
      featureContext,
      activeView,
      locale,
      i18nLocale,
      globalFields,
      fields,
      isAiButtonVisible,
    }): FormattingTabProps & WithI18nLocale => {
      const {
        perspectivesRelatedData,
        formattingTab,
        availableRelatedData,
        datasetRelatedData,
      } = state;

      const isNewConditionalFormatting = true;

      let { conditionalFormattingRules: rawConditionalFormattingRules } =
        formattingTab;

      const { referenceTypeNames, componentTypeNames } =
        perspectivesRelatedData;

      const componentTypeNamesSet = datasetRelatedData
        ? datasetRelatedData.componentTypeNames
        : new Set(componentTypeNames);

      const referenceTypeNamesSet = datasetRelatedData
        ? datasetRelatedData.referenceTypeNames
        : new Set(referenceTypeNames);

      rawConditionalFormattingRules = normalizeFormattingRules(
        rawConditionalFormattingRules
      );

      rawConditionalFormattingRules = filterValidConditionalFormattingRules(
        rawConditionalFormattingRules,
        { componentTypeNamesSet, referenceTypeNamesSet }
      );

      rawConditionalFormattingRules = ensureCollapsedForUnsupportedRules(
        rawConditionalFormattingRules
      );

      const fieldDataByRuleId = buildFieldDataByRuleId(
        rawConditionalFormattingRules,
        globalFields.byName,
        getNonTemplateFieldsByName(fields.byName)
      );

      const conditionalFormattingRules = normalizeLegacyCheckboxComparator(
        rawConditionalFormattingRules,
        fieldDataByRuleId,
        locale
      );

      const {
        availableWorkspaceTags,
        availableWorkspaceComponentFields,
        availableWorkspaceReferenceFields,
      } = availableRelatedData;

      const { tagOptions, asyncLabelLoaders, asyncSuggestionsLoaders } =
        getFilterTabProps(
          {
            ...perspectivesRelatedData,
            relatedTags: availableWorkspaceTags,
          },
          i18nLocale
        );

      const datasetComponentFieldNames =
        datasetRelatedData?.componentFieldNames;
      const datasetReferenceFieldNames =
        datasetRelatedData?.referenceFieldNames;

      const componentIds = datasetRelatedData?.componentIds ?? [];
      const referenceIds = datasetRelatedData?.referenceIds ?? [];
      const componentTypes = getComponentTypeDetails(Array.from(componentIds));
      const referenceTypes = getReferenceTypeDetails(Array.from(referenceIds));

      const relatedComponentFields = datasetRelatedData
        ? getFieldsByComponentTypes(componentTypes, datasetComponentFieldNames)
        : availableWorkspaceComponentFields;
      const relatedReferenceFields = datasetRelatedData
        ? getFieldsByReferenceTypes(referenceTypes, datasetReferenceFieldNames)
        : availableWorkspaceReferenceFields;

      const componentsFormattingGroups = composeComponentFormattingGroups({
        i18nLocale,
        relatedFields: relatedComponentFields,
        componentTypeNames,
        isNewConditionalFormatting,
        locale,
      });

      const referencesFormattingGroups = composeReferencesFormattingGroups({
        i18nLocale,
        relatedFields: relatedReferenceFields,
        referenceTypeNames,
        isNewConditionalFormatting,
        locale,
      });

      const formattingForInvalidRuleByRuleId =
        getFormattingForInvalidRuleByRuleId({
          conditionalFormattingRules,
          componentsFormattingGroups,
          referencesFormattingGroups,
          fieldDataByRuleId,
          locale,
        });

      const getFormattingForInvalidRule = (ruleId: string) =>
        formattingForInvalidRuleByRuleId[ruleId];

      const getRuleNotificationParams =
        getFormattingRuleNotificationParamsFunction({
          componentsFormattingGroups,
          referencesFormattingGroups,
          getFormattingForInvalidRule,
          isNewConditionalFormatting: false,
          datasetComponentTypeNamesSet: datasetRelatedData?.componentTypeNames,
          datasetReferenceTypeNamesSet: datasetRelatedData?.referenceTypeNames,
          i18nLocale,
        });

      const addRuleButtonOptions = getAddFormattingRuleButtonOptions({
        onFormattingRuleAdded,
        referencesFormattingGroups,
        tagsFormattingGroups: tagOptions,
        getDisabledOptionPopoverContent:
          getAddButtonDisabledPopoverContent(i18nLocale),
        isNewConditionalFormatting,
        i18nLocale,
      });

      const resolvedComponentTypeNames = datasetRelatedData
        ? Array.from(datasetRelatedData.componentTypeNames)
        : componentTypeNames;

      const onAiButtonClicked = () =>
        dispatchAction(conditionalFormattingAiButtonClicked());

      const description = t(
        i18nLocale,
        'perspectiveSidebar:formattingTab.description',
        'Set rules to automatically highlight components and references with colors.'
      );
      const tabDescription = {
        description,
      };

      const workspaceIds = perspectivesRelatedData.relatedWorkspaces.map(
        workspace => workspace._id
      );

      const componentNameAndFieldsIndex = buildComponentNameAndFieldsIndex(
        componentTypes,
        fieldInterface.getFieldsByComponentType,
        datasetComponentFieldNames
      );
      const referenceNameAndFieldsIndex = buildReferenceNameAndFieldsIndex(
        referenceTypes,
        fieldInterface.getFieldsByReferenceType,
        datasetReferenceFieldNames
      );

      const filterGroupsByRuleId = mapFilterGroups({
        i18nLocale,
        conditionalFormattingRules,
        componentsFormattingGroups,
        referencesFormattingGroups,
        formattingForInvalidRuleByRuleId,
        componentNameAndFieldsIndex,
        referenceNameAndFieldsIndex,
        isNewConditionalFormatting,
      });
      const formattingByRuleAndField = mapFormatting(filterGroupsByRuleId);
      const listFieldFormattingByRuleId = mapListFieldFormatting(
        conditionalFormattingRules,
        formattingByRuleAndField
      );
      const fieldTypesByName = toFieldTypeRecord(fields.byName);
      const ruleInfo = mapFormattingRuleInfo(
        i18nLocale,
        conditionalFormattingRules,
        filterGroupsByRuleId,
        formattingByRuleAndField,
        listFieldFormattingByRuleId,
        isNewConditionalFormatting,
        fieldTypesByName
      );

      const onRuleUpdated = (
        updatedFormattingRuleId: string,
        updatedFormattingRulePayload: Partial<FormattingRule>
      ) => {
        const ruleFieldFormatting =
          ruleInfo?.[updatedFormattingRuleId]?.fieldFormatting;

        const fieldFormattingData = {
          i18nLocale,
          updatedFormattingRulePayload,
          componentsFormattingGroups,
          referencesFormattingGroups,
          formattingForInvalidRuleByRuleId,
          componentNameAndFieldsIndex,
          referenceNameAndFieldsIndex,
          isNewConditionalFormatting,
          viewId: activeView.mainViewId,
        };

        return onFormattingRuleUpdated(
          updatedFormattingRuleId,
          ensureUpdatedRuleValue({
            previousFormattingRules: conditionalFormattingRules,
            updatedFormattingRuleId,
            updatedFormattingRulePayload,
            ruleFieldFormatting,
            fieldFormattingData,
          })
        );
      };

      const getEntityFieldValue = (
        entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
        entityId: string,
        fieldName: string
      ): unknown =>
        entityType === APIEntityType.COMPONENT
          ? (componentInterface.get(entityId)?.[fieldName] ?? null)
          : (referenceInterface.get(entityId)?.[fieldName] ?? null);

      const colorPaletteAlignment = featureOperations.hasFeature(
        featureContext,
        Features.CANVAS_PROTOTYPE
      )
        ? 'left'
        : 'right';

      return {
        conditionalFormatting: {
          formattingRules: conditionalFormattingRules,
          onFormattingRuleDeleted,
          onFormattingRuleReordered,
          onFormattingRuleUpdated: onRuleUpdated,
          onKeepOriginalColorToggled,
          asyncSuggestionsLoaders: {
            ...asyncSuggestionsLoaders,
            ...getViewpointComponentSuggestionLoaders(
              workspaceIds,
              componentTypeNames
            ),
            ...getViewpointReferenceSuggestionLoaders(workspaceIds),
          },
          asyncLabelLoaders,
          tagsFormattingGroups: tagOptions,
          getRuleNotificationParams,
          getFormattingForInvalidRule,
          addRuleButtonOptions,
          isAiButtonVisible,
          onAiButtonClicked,
          ruleInfo,
          componentTypeNames: resolvedComponentTypeNames,
          referenceTypeNames: datasetRelatedData
            ? Array.from(datasetRelatedData.referenceTypeNames)
            : referenceTypeNames,
          datasetData: {
            componentIds: Array.from(componentIds),
            referenceIds: Array.from(referenceIds),
          },
          getEntityFieldValue,
          viewId: activeView.mainViewId,
          locale,
          i18nLocale,
          keepOriginalColor: formattingTab.keepOriginalColor,
          colorPaletteAlignment,
        },
        tabDescription,
        i18nLocale,
      };
    }
  )
);

export { formattingTab$ };
